import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import {
  douyinMedian,
  douyinWeekday,
  filterSessions,
  groupSessionsByDate,
  type DouyinSessionTypeFilter,
} from '../douyinCommerce'
import { DouyinStatisticsBar, douyinStatisticsSearch, douyinStatisticsWindow, useDouyinStatisticsPeriod } from '../douyinStatistics'
import { douyinPath, useDouyinStoreId } from '../douyinStore'
import type { DouyinLiveAnalysis, DouyinSession } from '../douyinTypes'
import { integer, money } from '../utils'
import { DouyinBoundary, DySigned, DyTag, dyDuration, dyPct1, dyYuan0 } from './douyinCommon'

export function DouyinLiveSessionsPage() {
  return <DouyinBoundary>{(data) => <DouyinLiveSessionsContent data={data} />}</DouyinBoundary>
}

const sum = (values: Array<number | null | undefined>) => values.reduce<number>((total, value) => total + (value || 0), 0)

function DouyinLiveSessionsContent({ data }: { data: DouyinLiveAnalysis }) {
  const navigate = useNavigate()
  const storeId = useDouyinStoreId()
  const [period, setPeriod] = useDouyinStatisticsPeriod('day')
  const window = douyinStatisticsWindow(data.meta, period)
  const statisticsSearch = douyinStatisticsSearch(period)
  const [type, setType] = useState<DouyinSessionTypeFilter>('all')

  const all = useMemo(
    () => window ? filterSessions(data, window, 'all') : [],
    [data, window?.startDate, window?.endDate], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const selfSessions = all.filter((session) => session.type !== 'partner')
  const partnerCount = all.length - selfSessions.length
  const paid = sum(all.map((session) => session.paid))
  const ad = sum(all.map((session) => session.ad))
  const cost = sum(all.map((session) => session.cost))
  const gross = data.meta.profitReady === false ? null : sum(all.map((session) => session.gross))
  const afterAd = data.meta.profitReady === false ? null : sum(all.map((session) => session.afterAd))
  const refund = sum(all.map((session) => session.refund))
  const nocost = sum(all.map((session) => session.nocost))
  const selfPaid = selfSessions.map((session) => session.paid || 0)
  const selfMedian = douyinMedian(selfPaid)

  const filteredSessions = useMemo(
    () => window ? filterSessions(data, window, type) : [],
    [data, type, window?.startDate, window?.endDate], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const groups = useMemo(() => groupSessionsByDate(filteredSessions), [filteredSessions])
  const openDetail = (sessionKey: string) => navigate(`${douyinPath(storeId, `/live/${encodeURIComponent(sessionKey)}`)}${statisticsSearch}`)

  return <div className="page-stack dy-page">
    <div className="page-title-row">
      <div><span className="dy-platform-label">DOUYIN LIVE</span><h1>直播场次</h1><p>{window ? `${window.label} · ${integer(all.length)} 场 · 自营 ${integer(selfSessions.length)} · 达人 ${integer(partnerCount)}` : '直播数据加载中'}</p></div>
    </div>
    <DouyinStatisticsBar period={period} onChange={setPeriod} window={window} />
    <div className="pdd-caliber-note">口径说明：{data.meta.caliber || '千川消耗取罗盘「投放消耗(店铺绑定)」；大场 = 单场支付 ≥ 10 万，仅标注不剔除。'}</div>
    <MetricStrip items={[
      { label: '场次数', value: `${integer(all.length)} 场`, context: `自营 ${integer(selfSessions.length)} · 达人 ${integer(partnerCount)}` },
      { label: '用户支付金额', value: dyYuan0(paid), context: '主轴口径' },
      { label: '自营单场中位数', value: selfPaid.length ? dyYuan0(selfMedian) : '—', context: selfPaid.length ? `自营均值 ${dyYuan0(sum(selfPaid) / selfPaid.length)}` : '范围内无自营场' },
      { label: '千川消耗', value: dyYuan0(ad), context: `投产比 ${ad ? (paid / ad).toFixed(1) : '—'}（支付÷千川）` },
      { label: '产品成本', value: dyYuan0(cost), context: `缺成本 ${dyYuan0(nocost)}（不摊）` },
      { label: '估算毛利', value: dyYuan0(gross), context: gross != null && paid ? `毛利率 ${dyPct1(gross / paid)}，未含包装/赠品费` : '成本口径待确认' },
      { label: '广告后利润', value: dyYuan0(afterAd), context: '估算毛利 − 千川' },
      { label: '退款金额', value: dyYuan0(refund), context: '不进利润，单列' },
    ]} />
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>场次明细</h2><p>按开播日分组 · 组头行为当日小计 · 点击行查看单场详情</p></div><span>共 {integer(filteredSessions.length)} 场 · 支付合计 {money(sum(filteredSessions.map((session) => session.paid)))}</span></div>
      <div className="filter-bar">
        <label><span className="sr-only">场次类型</span><select value={type} onChange={(event) => setType(event.target.value as DouyinSessionTypeFilter)}>
          <option value="all">类型：全部</option>
          <option value="self">自营</option>
          <option value="partner">达人合作</option>
        </select></label>
      </div>
    </section>
    <section className="overview-section">
      <div className="table-responsive"><table className="product-table dy-table dy-live-table"><thead><tr>
        <th className="l">开播</th><th>时长</th><th>支付金额</th><th>件数 / 订单</th><th>千川消耗</th><th>投产比</th><th>产品成本</th><th>估算毛利</th><th>广告后利润</th><th>返点金额</th><th>含返点广告后利润</th><th>退款金额</th><th className="l">主要商品</th>
      </tr></thead><tbody>{groups.map((group) => <SessionGroupRows key={group.date} date={group.date} sessions={group.sessions} onOpen={openDetail} />)}</tbody></table></div>
    </section>
  </div>
}

function SessionGroupRows({ date, sessions, onOpen }: { date: string; sessions: DouyinSession[]; onOpen: (key: string) => void }) {
  return <>
    <tr className="dy-group-hd"><td className="l" colSpan={13}>
      {date} 周{douyinWeekday(date)}
      <span className="dy-group-sub">{integer(sessions.length)} 场 · 支付 {money(sum(sessions.map((session) => session.paid)))} · 千川 {money(sum(sessions.map((session) => session.ad)))} · 估算毛利 {sessions.every((session) => session.gross == null) ? '—' : money(sum(sessions.map((session) => session.gross)))} · 返点 {sessions.every((session) => session.rebate == null) ? '—' : money(sum(sessions.map((session) => session.rebate)))} · 含返点广告后 {sessions.every((session) => session.afterAdWithRebate == null) ? '—' : money(sum(sessions.map((session) => session.afterAdWithRebate)))}</span>
    </td></tr>
    {sessions.map((session) => {
      const roi = session.ad ? (session.paid / session.ad).toFixed(1) : '—'
      const top = (session.top || []).slice(0, 2)
      return <tr className="dy-clickable-row" key={session.k} onClick={() => onOpen(session.k)}>
        <td className="l">
          {session.start}{' '}
          {session.type === 'partner' ? <DyTag kind="partner">达人</DyTag> : <DyTag kind="norm">自营</DyTag>}
          {session.big ? <> <DyTag kind="big">大场</DyTag></> : null}
        </td>
        <td>{dyDuration(session.dur)}</td>
        <td><b>{money(session.paid)}</b></td>
        <td>{integer(session.items)} / {integer(session.orders)}</td>
        <td>{money(session.ad)}</td>
        <td>{roi}</td>
        <td>{money(session.cost)}</td>
        <td><DySigned value={session.gross} /></td>
        <td><DySigned value={session.afterAd} /></td>
        <td><DySigned value={session.rebate} /></td>
        <td><DySigned value={session.afterAdWithRebate} /></td>
        <td>{session.refund ? money(session.refund) : '—'}</td>
        <td className="l dy-topn">{top.length
          ? top.map((topItem, index) => <span key={`${topItem.n}-${index}`} title={topItem.n}>
            {index > 0 ? <span className="dy-faint"> · </span> : null}
            {(topItem.n || '').replace(/^BEDDY\.POP/, '').slice(0, 10)} {dyYuan0(topItem.paid)}
          </span>)
          : <span className="dy-faint">—</span>}</td>
      </tr>
    })}
  </>
}
